import { Entypo } from '@expo/vector-icons';
import { router } from 'expo-router';
import { Platform, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native';

import { FavoriteType } from '@/api/models/favoritesModels';
import Text from '@/components/Text';
import { firebaseImage, ThumbSize } from '@/utils/firebaseImage';

import FavoriteButton from './buttons/FavoriteButton';
import ShareButton from './buttons/ShareButton';
import DetailsHeaderCarousel from './DetailsHeaderCarousel';
import DetailsHeaderGallery from './web/DetailsHeaderGallery.web';

interface DetailsHeaderProps {
  type: FavoriteType;
  thumbnails: string[];
  imageType?: 'churchToClaim' | 'editedNotClaimed' | 'default';
  title?: string;
  subtitle?: string;
  animatedOpacityStyle: { opacity: number };
  itemId: string;
  lightRatingValues?: { ranking: number | undefined; name: string };
}

const DetailsHeader = ({
  thumbnails,
  imageType = 'default',
  title,
  subtitle,
  animatedOpacityStyle,
  itemId,
  type,
  lightRatingValues
}: DetailsHeaderProps) => {
  const { width: windowWidth } = useWindowDimensions();

  return (
    <>
      {Platform.OS !== 'web' && (
        <TouchableOpacity onPress={() => {
          if (router.canDismiss()) {
            router.back();
          } else {
            router.replace('/(app)/(bottomTabs)/home/(topTabs)/npoTabs')
          }
        }} style={style.backButton}>
          <Entypo size={18} name="chevron-thin-left" style={style.roundedIcon} />
        </TouchableOpacity>
      )}

      <View style={style.shareButton}>
        <ShareButton variant="rounded" shareThumb={firebaseImage(thumbnails?.at(0), ThumbSize.Small)} />
      </View>

      <View style={style.favoriteButton}>
        {/* On web the hearth icon is larger than the other buttons */}
        <FavoriteButton
          size={Platform.OS === 'web' ? 17 : 18}
          style={style.roundedIcon}
          variant="outlined"
          itemId={itemId}
          type={type}
        />
      </View>

      {/* Carousel/Gallery */}
      {Platform.OS === 'web' && windowWidth > 768 && thumbnails.length > 1 && imageType === 'default' ? (
        <DetailsHeaderGallery
          thumbnails={thumbnails}
          animatedOpacityStyle={animatedOpacityStyle ?? { opacity: 1 }}
          lightRatingValues={lightRatingValues}
        />
      ) : (
        <DetailsHeaderCarousel
          thumbnails={thumbnails}
          imageType={imageType}
          animatedOpacityStyle={animatedOpacityStyle ?? { opacity: 1 }}
          lightRatingValues={lightRatingValues}
        />
      )}

      {title && (
        <Text fontWeight="500" style={[style.boldText, style.title, style.headerTitle]}>
          {title}
        </Text>
      )}
      {subtitle && (
        <Text fontWeight="500" style={style.subtitle}>
          {subtitle}
        </Text>
      )}
    </>
  );
};

export default DetailsHeader;

const top = Platform.OS === 'web' ? 15 : 60;

const style = StyleSheet.create({
  backButton: {
    position: 'absolute',
    top: 60,
    left: 15,
    zIndex: 10,
    elevation: 10
  },

  shareButton: {
    position: 'absolute',
    top: top,
    right: 65,
    zIndex: 10,
    elevation: 10
  },

  favoriteButton: {
    position: 'absolute',
    top: top,
    right: 15,
    zIndex: 10,
    elevation: 10
  },

  roundedIcon: {
    color: 'black',
    backgroundColor: 'white',
    //(Size + Padding + BorderWidth) / 2
    borderRadius: (18 + 16) / 2,
    padding: 8,
    overflow: 'hidden'
  },

  headerTitle: {
    paddingTop: 25,
    paddingBottom: 10,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0
  },

  boldText: {
    color: 'black'
  },

  title: {
    fontSize: 22
  },

  subtitle: {
    fontSize: 14,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0,
    paddingBottom: 5,
    color: 'black'
  }
});
